import React, { useRef, useCallback } from 'react';
import Turnstile from 'react-turnstile';
import apiClient from '../services/api';

/**
 * TurnstileChallenge -- a modal overlay that shows when the backend returns
 * `turnstile_challenge: true` (403). The user solves the Turnstile widget,
 * the token is sent to /api/auth/verify-session, and on success the overlay
 * is dismissed and any pending retry callback is executed.
 */
const TurnstileChallenge = ({ onVerified, onDismiss }) => {
  const turnstileRef = useRef(null);

  const handleVerify = useCallback(async (token) => {
    try {
      const response = await apiClient.post('/auth/verify-session', {
        turnstileToken: token,
      });
      if (response.token) {
        localStorage.setItem('token', response.token);
      }
      if (onVerified) {
        onVerified();
      }
    } catch (err) {
      console.error('[TurnstileChallenge] Verification failed:', err);
      // Reset the widget so the user can try again
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
    }
  }, [onVerified]);

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2 style={styles.title}>Session Verification Required</h2>
        <p style={styles.description}>
          We detected a change in your connection. Please complete the verification
          below to continue.
        </p>
        <div style={styles.widgetContainer}>
          <Turnstile
            ref={turnstileRef}
            sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
            onVerify={handleVerify}
            onError={() => {
              console.error('[TurnstileChallenge] Widget error');
            }}
          />
        </div>
        {onDismiss && (
          <button onClick={onDismiss} style={styles.cancelBtn}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  modal: {
    background: 'white',
    borderRadius: '12px',
    padding: '2rem',
    maxWidth: '460px',
    width: '90%',
    textAlign: 'center',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: '0.75rem',
  },
  description: {
    fontSize: '0.95rem',
    color: '#7f8c8d',
    marginBottom: '1.5rem',
    lineHeight: '1.5',
  },
  widgetContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1rem',
  },
  cancelBtn: {
    background: 'none',
    border: '1px solid #dfe6e9',
    padding: '0.5rem 1.5rem',
    borderRadius: '6px',
    fontSize: '0.9rem',
    color: '#7f8c8d',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
};

export default TurnstileChallenge;
